/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { throttle } from 'lodash';
import {
  getBurndownData,
  getCumulativeFlowSeries,
  getLeanSeries,
  getVelocityData
} from '../../actions/teamActions';
import BurndownLegend from '../Graph/burndownLegend';
import { onResizeDone } from '../../utils/browserHelpers';
import { isKanbanMode } from '../../selectors/teamSelector';
import generateGraph from '../../utils/burndown';
import VrsDropdown from '../PlanWall/vrsDropdown';
import CumulativeFlow from '../WorkPackage/cumulativeFlow';
import Lean from '../WorkPackage/lean';
import Velocity from '../Graph/velocity';
import DateRange from '../Common/dateRange';
import DropDownMenu from '../Common/dropDownMenu';
import CustomSelect from '../Common/CustomSelect/SelectField';
import { truncate } from '../../utils/string';

class Metrics extends React.Component {
  constructor() {
    super();

    this.burndownGraph = null;

    this.burndownDiv = React.createRef();
    this.cumulativeFlowDiv = React.createRef();
    this.leanDiv = React.createRef();
    this.velocityDiv = React.createRef();

    this.state = {
      isFetchingGraphs: {
        lean: false,
        burndown: false,
        velocity: false,
        cumulative: false
      },
      sprintSelected: 0,
      startDate: moment().subtract(3, 'M'),
      endDate: moment(),
      dropDown: {},
      resultType: {
        risk: '',
        size: '',
        value: ''
      },
      velocitySprints: 3,
      resizing: false,
      containerHeight: 0,
      isFull: {
        lean: false,
        burndown: false,
        cumulative: false,
        velocity: false
      }
    };
  }

  componentDidMount() {
    this.calculateChartHeight();
    const { sprints = [] } = this.props;
    if (sprints.length > 0) {
      this.setState({ sprintSelected: sprints[sprints.length - 1].id }, () =>
        this.getMetrics()
      );
    } else {
      this.getMetrics();
    }
    window.addEventListener('resize', this.calculateChartHeight);
    onResizeDone(
      true,
      'resizeCharts',
      this.handleResize,
      this.stopResizing,
      300
    );
  }

  componentDidUpdate() {
    const { isFetchingGraphs } = this.state;
    const { metrics, isKanban } = this.props;

    if (isFetchingGraphs.burndown && metrics.burndown && !isKanban) {
      this.burndownGraph = generateGraph(
        metrics.burndown,
        this.state.containerHeight - 55 // 70 original
      );
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, burndown: false }
      });
    }
    if (isFetchingGraphs.lean && metrics.lean) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, lean: false }
      });
    }
    if (isFetchingGraphs.velocity && metrics.velocity && !isKanban) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, velocity: false }
      });
    }
    if (isFetchingGraphs.cumulative && metrics.cumulative) {
      this.setState({
        isFetchingGraphs: { ...isFetchingGraphs, cumulative: false }
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.calculateChartHeight);
    onResizeDone(false, 'resizeCharts');
  }

  handleResize = throttle(() => {
    this.setState({ resizing: true });
  }, 100);

  stopResizing = () => {
    const { isFull } = this.state;

    this.setState({ resizing: false });
    this.burndownGraph = generateGraph(
      this.props.metrics.burndown,
      this.state.containerHeight - (isFull.burndown ? 90 : 55)
    );
  };

  calculateChartHeight = () => {
    const containerHeight = document.querySelector('.flex-row').offsetHeight;
    this.setState({ containerHeight });
  };

  getMetrics = () => {
    const { team, isKanban } = this.props;
    const {
      isFetchingGraphs,
      sprintSelected,
      resultType,
      velocitySprints,
      startDate,
      endDate
    } = this.state;
    if (team && team.id) {
      if (!isFetchingGraphs.burndown && sprintSelected > 0 && !isKanban) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, burndown: true }
        }));
        this.props.getBurndownData(team.id, sprintSelected);
      }
      if (!isFetchingGraphs.cumulative) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, cumulative: true }
        }));
        this.props.getCumulativeFlowSeries(
          team.id,
          startDate.format('YYYY-MM-DD'),
          endDate.format('YYYY-MM-DD')
        );
      }
      if (!isFetchingGraphs.lean) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, lean: true }
        }));
        this.props.getLeanSeries(team.id, resultType);
      }
      if (!isFetchingGraphs.velocity && !isKanban) {
        this.setState((prevState) => ({
          isFetchingGraphs: { ...prevState.isFetchingGraphs, velocity: true }
        }));
        this.props.getVelocityData(team.id, velocitySprints);
      }
    }
  };

  setIsFetching = (name) => {
    this.setState((prevState) => ({
      isFetchingGraphs: { ...prevState.isFetchingGraphs, [name]: true }
    }));
  };

  changeSprint = (option) => {
    this.setState({ sprintSelected: option.value }, () => {
      this.setIsFetching('burndown');
      this.props.getBurndownData(this.props.team.id, this.state.sprintSelected);
    });
  };

  changeVelocitySprints = (e) => {
    this.setState({ velocitySprints: Number(e.target.value) });
  };

  handleCumulativeDateChange = ({ start_date: start, end_date: end }) => {
    if (start) {
      this.setState({ startDate: moment(start) }, () => {
        this.setIsFetching('cumulative');
        this.props.getCumulativeFlowSeries(
          this.props.team.id,
          this.state.startDate.format('YYYY-MM-DD'),
          this.state.endDate.format('YYYY-MM-DD')
        );
      });
    }
    if (end) {
      this.setState({ endDate: moment(end) }, () => {
        this.setIsFetching('cumulative');
        this.props.getCumulativeFlowSeries(
          this.props.team.id,
          this.state.startDate.format('YYYY-MM-DD'),
          this.state.endDate.format('YYYY-MM-DD')
        );
      });
    }
  };

  handleResultTypeChange = (type, newValue) => {
    const { team } = this.props;
    const { resultType } = this.state;
    resultType[type] = newValue.type_attributes[type];
    this.setState({ resultType }, () => {
      this.setIsFetching('lean');
      this.props.getLeanSeries(team.id, resultType);
    });
  };

  resetResultType = () => {
    const { team } = this.props;
    const initialResultType = {
      risk: '',
      size: '',
      value: ''
    };

    this.setState({ resultType: initialResultType }, () => {
      this.setIsFetching('lean');
      this.props.getLeanSeries(team.id, initialResultType);
    });
  };

  isResetButtonActive = () => {
    const {
      resultType: { risk, size, value }
    } = this.state;
    return !risk && !size && !value;
  };

  toggleDropDownMenu = (e, type, props) => {
    e.stopPropagation();
    let { dropDown } = this.state;
    if (dropDown.id) {
      dropDown = {};
    } else {
      const newProps = {
        ...props
      };
      const { target: element } = e;
      dropDown = {
        ...newProps,
        type,
        elementClicked: element,
        elementAttached: element.closest('.dropdown'),
        parent: element.parentNode,
        id: type,
        closeDropdown: () => this.setState({ dropDown: {} })
      };
    }
    this.setState({ dropDown });
  };

  generateSprintOptions = () => {
    const { sprints } = this.props;
    return sprints.map((sprint) => ({
      label: truncate(sprint.short_name || '', 23),
      value: sprint.id
    }));
  };

  resizeGraphsWhenFull = (chart) => {
    const { isFull } = this.state;
    if (chart === 'burndown') {
      const burndownWidth = this.burndownDiv.current
        ? this.burndownDiv.current.offsetWidth
        : 0;

      this.calculateChartHeight();
      this.burndownGraph.resize({
        width: burndownWidth,
        height:
          this.burndownDiv.current.offsetHeight - (isFull.burndown ? 90 : 55)
      });
    }

    if (chart === 'cumulative') {
      if (!isFull.cumulative) {
        this.calculateChartHeight();
        this.burndownGraph = generateGraph(
          this.props.metrics.burndown,
          this.cumulativeFlowDiv.current.offsetHeight - 55
        );
      }

      if (isFull.cumulative) {
        this.calculateChartHeight();
      }
    }

    if (chart === 'velocity') {
      // This effectively forces the component to rerender twice.
      // Is it hacky? Yes
      // Does it fix my issue? Yes
      // Do I know why this works? No damn clue
      // Without this, the velocity chart doesnt resize properly

      this.setState(
        {
          forceUpdate: true
        },
        this.forceUpdate
      );
    }
  };

  velocityChartWidth = (leanWidth, velocityWidth) => {
    const { isFull } = this.state;
    if (isFull.velocity) {
      return velocityWidth;
    }
    // the width of the velocity chart is based off of the lean width when the lean chart is present
    return leanWidth - 50 > 0 ? leanWidth - 50 : 0;
  };

  toggleIsFull = (chart) => {
    const { isFull } = this.state;
    this.setState(
      (prevState) => ({
        isFull: { ...isFull, [chart]: !prevState.isFull[chart] }
      }),
      () => {
        this.resizeGraphsWhenFull(chart);
      }
    );
  };

  render() {
    const { metrics, sprints, team, defaultOptions, isKanban } = this.props;
    const {
      isFetchingGraphs,
      startDate,
      endDate,
      dropDown,
      velocitySprints,
      containerHeight,
      resizing,
      sprintSelected,
      isFull
    } = this.state;
    const isLoading = Object.values(isFetchingGraphs).some(
      (fetching) => fetching
    );
    const cumulativeFlowWidth = this.cumulativeFlowDiv.current
      ? this.cumulativeFlowDiv.current.offsetWidth
      : 0;
    const leanWidth = this.leanDiv.current
      ? this.leanDiv.current.offsetWidth
      : 0;

    const velocityWidth = this.velocityDiv.current
      ? this.velocityDiv.current.offsetWidth
      : 0;

    if (!metrics) {
      return null;
    }

    const sprintOptions = this.generateSprintOptions();
    const selectedOption =
      sprintOptions.find((option) => option.value === sprintSelected) ||
      sprintOptions[sprintOptions.length - 1];

    return (
      <div
        style={{ overflowY: 'auto', overflowX: 'hidden' }}
        className={`${isLoading ? 'blur-loading' : ''} team-metrics-tab`}
      >
        {isLoading && <div id="loader" />}
        <div className="row flex-row">
          {sprints.length > 0 && !isKanban && !isFull.cumulative && (
            <div
              className={`team-burndown-container col-sm-${
                isFull.burndown ? '12' : '6'
              }`}
              ref={this.burndownDiv}
              onClick={() => this.toggleIsFull('burndown')}
            >
              {!resizing && (
                <div
                  className="metrics-sprint-selection"
                  onClick={(e) => e.stopPropagation()}
                >
                  <label>Select Sprint</label>
                  <CustomSelect
                    options={sprintOptions}
                    handleOptionChange={this.changeSprint}
                    selectedOption={selectedOption}
                    containerClass="team-metrics-tab"
                  />
                </div>
              )}
              {!resizing && <div id="chart" />}
              <BurndownLegend burndown={metrics.burndown} />
            </div>
          )}
          {!isFull.burndown && (
            <div
              className={`team-cumulative-container col-sm-${
                isFull.cumulative ? '12' : '6'
              } graph-container result-cms`}
              ref={this.cumulativeFlowDiv}
              onClick={() => this.toggleIsFull('cumulative')}
            >
              {!isFetchingGraphs.cumulative && !resizing && (
                <>
                  <div
                    className="metrics-date-selectors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label>Select Dates</label>
                    <DateRange
                      startDate={startDate.format('MM/DD/YY')}
                      endDate={endDate.format('MM/DD/YY')}
                      onDateChange={this.handleCumulativeDateChange}
                    />
                  </div>
                  <CumulativeFlow
                    graphData={metrics.cumulative}
                    team={team}
                    width={cumulativeFlowWidth}
                    height={containerHeight - 67 > 0 ? containerHeight - 67 : 0}
                    teamTab
                  />
                </>
              )}
            </div>
          )}
        </div>
        {!isFull.burndown && !isFull.cumulative && (
          <div className="row static-row">
            {!isFull.velocity && (
              <div className="team-lean-container" ref={this.leanDiv}>
                {!resizing && (
                  <>
                    <div className="vrs-dropdown-container">
                      <span>Select </span>
                      <div className="inner-vrs-container">
                        <button
                          className="vrs-all-button"
                          onClick={this.resetResultType}
                          disabled={this.isResetButtonActive()}
                        >
                          ALL
                        </button>
                        <VrsDropdown
                          type="value"
                          data={this.state.resultType}
                          level={1}
                          options={
                            defaultOptions && defaultOptions.value_defaults
                              ? defaultOptions.value_defaults.values
                              : {}
                          }
                          handleSelect={(newValue) =>
                            this.handleResultTypeChange('value', newValue)
                          }
                          toggleDropDownMenu={this.toggleDropDownMenu}
                        />
                        <VrsDropdown
                          type="risk"
                          data={this.state.resultType}
                          level={1}
                          options={
                            defaultOptions && defaultOptions.risk_defaults
                              ? defaultOptions.risk_defaults.values
                              : {}
                          }
                          handleSelect={(newValue) =>
                            this.handleResultTypeChange('risk', newValue)
                          }
                          toggleDropDownMenu={this.toggleDropDownMenu}
                        />
                        <VrsDropdown
                          type="size"
                          data={this.state.resultType}
                          level={1}
                          options={
                            defaultOptions && defaultOptions.size_defaults
                              ? defaultOptions.size_defaults.values
                              : {}
                          }
                          handleSelect={(newValue) =>
                            this.handleResultTypeChange('size', newValue)
                          }
                          toggleDropDownMenu={this.toggleDropDownMenu}
                        />
                      </div>
                      {dropDown && dropDown.id && (
                        <DropDownMenu
                          type={dropDown.type}
                          dropDown={dropDown}
                          key={dropDown.id}
                        />
                      )}
                    </div>
                    <Lean
                      graphData={metrics.lean}
                      teamData
                      defaultOptions={defaultOptions}
                      team={team}
                      width={leanWidth - 160 > 0 ? leanWidth - 160 : 0}
                      height={140}
                      resultType={this.state.resultType}
                    />
                    &nbsp;
                  </>
                )}
              </div>
            )}
            {!isKanban && sprints.length > 0 && (
              <div
                className={`team-velocity-container ${
                  isFull.velocity ? 'isFull' : ''
                }`}
                ref={this.velocityDiv}
                onClick={() => this.toggleIsFull('velocity')}
              >
                {!isFetchingGraphs.velocity && !resizing && (
                  <Velocity
                    graphData={metrics.velocity}
                    team={team}
                    width={this.velocityChartWidth(leanWidth, velocityWidth)}
                    sprints={sprints.slice(0, 7)}
                    height={
                      this.velocityDiv.current?.offsetHeight - 60 > 140
                        ? this.velocityDiv.current?.offsetHeight - 60
                        : 140
                    }
                    sprintsSelected={velocitySprints}
                    changeVelocitySprints={this.changeVelocitySprints}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

Metrics.propTypes = {
  metrics: PropTypes.object
};

const mapStateToProps = (state, ownProps) => {
  const metrics =
    ownProps.team &&
    ownProps.team.id &&
    state.teamReducer.metrics[ownProps.team.id]
      ? state.teamReducer.metrics[ownProps.team.id]
      : {};

  return {
    metrics,
    defaultOptions: state.defaultOptionsReducer.defaultOptions,
    isKanban: isKanbanMode(state)
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getBurndownData,
      getLeanSeries,
      getCumulativeFlowSeries,
      getVelocityData
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Metrics);
